import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { Form } from "react-bootstrap";

// Default ceiling before the box stops growing and scrolls instead.
const MAX_ROWS = 6;

/**
 * Drop-in for <Form.Control as="textarea"> that grows with its content.
 *
 * Starts at `rows` lines, grows as text is typed up to `maxRows` (default 6),
 * then scrolls inside. A box that already starts taller than maxRows (e.g. the
 * 12-row intake summary) keeps its starting height as the ceiling, so nothing
 * shrinks. Shrinks back when the value is cleared (e.g. after sending).
 *
 * Forwards refs, so callers that focus/scroll to a field keep working.
 */
const AutoGrowTextarea = forwardRef(function AutoGrowTextarea(
  { rows = 2, maxRows = MAX_ROWS, value, style, ...props },
  ref
) {
  const innerRef = useRef(null);

  const setRefs = useCallback(
    (el) => {
      innerRef.current = el;
      if (typeof ref === "function") ref(el);
      else if (ref) ref.current = el;
    },
    [ref]
  );

  const resize = useCallback(() => {
    const el = innerRef.current;
    // Hidden (inactive tab, closed collapse): nothing to measure yet. The
    // ResizeObserver below re-runs this once it becomes visible.
    if (!el || el.offsetWidth === 0) return;

    const cs = window.getComputedStyle(el);
    const lineHeight = parseFloat(cs.lineHeight) || parseFloat(cs.fontSize) * 1.5;
    const padding = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const border = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);
    // Bootstrap uses border-box, so height includes padding + border.
    const minH = lineHeight * rows + padding + border;
    const maxH = lineHeight * Math.max(rows, maxRows) + padding + border;

    el.style.height = "auto";
    // scrollHeight includes padding but not border.
    const needed = el.scrollHeight + border;
    el.style.height = `${Math.min(Math.max(needed, minH), maxH)}px`;
    el.style.overflowY = needed > maxH ? "auto" : "hidden";
  }, [rows, maxRows]);

  useLayoutEffect(() => {
    resize();
  }, [value, resize]);

  // Re-fit when the width changes (window resize, phone rotation) or the box
  // goes from hidden to visible. Only reacts to width so our own height
  // changes don't re-trigger it.
  useEffect(() => {
    const el = innerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    let lastWidth = el.offsetWidth;
    const ro = new ResizeObserver(() => {
      if (el.offsetWidth !== lastWidth) {
        lastWidth = el.offsetWidth;
        resize();
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [resize]);

  return (
    <Form.Control
      {...props}
      as="textarea"
      ref={setRefs}
      rows={rows}
      value={value}
      style={{ resize: "none", ...style }}
    />
  );
});

export default AutoGrowTextarea;
